// pages/NoteView.jsx
// [Notes] Tampilan BERSIH satu catatan — dibuka via klik kanan → "Buka di tab
// baru" dari daftar Notes, atau langsung ke URL /note/<id>.
// Tanpa sidebar/header dashboard: hanya judul, tab bar (bila ada), dan isi.
// Tetap butuh login (PrivateRoute di App.js); data via GET /api/notes.
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { MarkdownView } from './Notes';

export default function NoteView() {
  const { noteId } = useParams();
  const [searchParams] = useSearchParams();
  const {
    lang,
    t
  } = useLang();
  const id = lang !== 'en';

  const [note, setNote]   = useState(undefined); // undefined=loading, null=tidak ditemukan
  const [tab, setTab]     = useState(() => {
    const t = parseInt(searchParams.get('ntab') || '0', 10);
    return Number.isInteger(t) && t >= 0 ? t : 0;
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axiosApi.get('/api/notes');
        if (!alive) return;
        const found = (res.data || []).find(n => String(n.id) === String(noteId)) || null;
        setNote(found);
        if (found) document.title = `${found.title || 'Note'} — Primary Dashboard`;
      } catch (_) { if (alive) setNote(null); }
    })();
    return () => { alive = false; };
  }, [noteId]);

  if (note === undefined) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (note === null) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
      <div>
        <div className="text-4xl mb-3">🗒️</div>
        <p className="text-slate-600 font-semibold mb-1">{t('noteView.noteNotFound')}</p>
        <p className="text-slate-400 text-sm">{t('noteView.mayHaveBeenDeletedLack')}</p>
      </div>
    </div>
  );

  const tabs = note.tabs || [];
  const content = tab === 0 ? note.content : (tabs[tab - 1]?.content || '');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
        {/* Header ringkas */}
        <div className="mb-4 pb-3 border-b border-slate-200">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 break-words">
            {note.title || (t('noteView.untitled'))}
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            {note.scope === 'common' ? (t('noteView.shared')) : (t('noteView.private'))}
            {note.updatedBy ? ` · ${t('noteView.by')} ${note.updatedBy}` : ''} · {note.updatedAt}
          </p>
        </div>

        {/* Tab bar (bila ada tabs) */}
        {tabs.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-4 print:hidden">
            {[{ title: note.title || (t('noteView.main')) }, ...tabs].map((t, idx) => (
              <button key={idx} onClick={() => setTab(idx)}
                className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs font-semibold max-w-[200px] transition-colors
                  ${tab === idx ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                <span className="truncate">{idx === 0 ? `📄 ${t.title}` : (t.title || `Tab ${idx}`)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 sm:px-8 py-6 print:border-0 print:shadow-none print:px-0">
          {content && content.trim()
            ? <MarkdownView source={content} />
            : <span className="text-slate-400 text-sm">{t('noteView.noContent')}</span>}
        </div>
      </div>
    </div>
  );
}
